import { createTheme } from '@mui/material/styles'

import { colorTokens } from './colorTokens'
import { FONT_FAMILY_SANS, typeScale } from './typography'
import { RADIUS } from './radii'

/**
 * MUI theme factory — 28 spec トークンを palette/typography/shape/components に移植。
 *
 * 一次情報: hub/log/28.../03_ui_implementation_spec.md
 * 移植元: nasu-patient-app-front/src/shared/styles/theme.ts (fontScale機構は本アプリ未導入のため簡略化)
 *
 * MUI Typography variant ↔ 28 spec typeScale 対応:
 *  h1-h4: 同名同値 / body1: body / body2: bodySm(14px) / button: label(13px) / caption: tag(12px)
 */
export const createLatestTheme = () =>
  createTheme({
    palette: {
      background: {
        default: colorTokens.bg.default,
        paper: colorTokens.bg.subtle,
      },
      text: {
        primary: colorTokens.fg.default,
        secondary: colorTokens.fg.muted,
        disabled: colorTokens.fg.faint,
      },
      primary: {
        main: colorTokens.accent.primary,
        dark: colorTokens.accent.primaryHover,
        contrastText: colorTokens.accent.primaryFg,
      },
      info: { main: colorTokens.state.infoFg, light: colorTokens.state.infoBg },
      warning: {
        main: colorTokens.state.warnFg,
        light: colorTokens.state.warnBg,
      },
      error: {
        main: colorTokens.state.dangerFg,
        light: colorTokens.state.dangerBg,
      },
      success: {
        main: colorTokens.state.successFg,
        light: colorTokens.state.successBg,
      },
      action: {
        selected: colorTokens.bg.muted,
        selectedOpacity: 0.06,
        hover: colorTokens.overlay.hover,
        hoverOpacity: 0.04,
        focus: colorTokens.overlay.active,
        focusOpacity: 0.08,
        disabled: colorTokens.fg.disabled,
        disabledBackground: colorTokens.bg.subtle,
      },
      divider: colorTokens.border.default,
    },
    typography: {
      fontFamily: FONT_FAMILY_SANS,
      h1: typeScale.h1,
      h2: typeScale.h2,
      h3: typeScale.h3,
      h4: typeScale.h4,
      body1: typeScale.body,
      body2: typeScale.bodySm,
      button: { ...typeScale.label, textTransform: 'none' as const },
      caption: typeScale.tag,
    },
    shape: { borderRadius: RADIUS.md },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiAppBar: { defaultProps: { elevation: 0 } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: RADIUS.full, textTransform: 'none' },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', minHeight: 28 } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: 'rgba(55, 53, 47, 0.16)' },
          root: {
            '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'rgba(55, 53, 47, 0.3)',
              },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: colorTokens.overlay.selected },
          },
        },
      },
    },
  })

export const latestTheme = createLatestTheme()

export { colorTokens }
